import { useState } from 'react';
import { Button, DialogShell, fieldClass, helpClass, labelClass } from '@timely/ui';
import { BAD_DEBT_MEANING, RANK_WORDS, bulkChangedLine, bulkDoneLine, bulkSkipLines, presetDay,
    type BulkAction, type BulkResult,
} from '@timely/domain';
import { FormNotice } from '../screens/AuthLayout';
import { DateField } from '../forms/DateField';
import { useFormatters } from './useFormatters';
import { bulkUpdateCustomers } from './useBook';

const TITLE: Record<BulkAction, (n: string) => string> = {
    collector: n => `Reassign ${n}`,
    follow_up: n => `Set the next follow-up for ${n}`,
    rank: n => `Set the payment rank for ${n}`,
};

const DONE: Record<BulkAction, string> = {
    collector: 'Collector changed',
    follow_up: 'Next follow-up set',
    rank: 'Payment rank set',
};

/**
 * One change to many accounts, in two steps (0033).
 *
 * First the person chooses the change, and "Check" asks the database what it
 * would do — the same function, with every check it will make, writing
 * nothing. The screen then says exactly that: how many change, and why the
 * rest will not. Only then is there a button that changes anything, and it
 * says how many. Every account changed gets its own line in its history, in
 * this person's name, and the batch an entry in the audit log.
 *
 * Changing the choice after a check throws the check away: a confirmation is
 * only ever of the change on screen.
 */
export function BulkChangeDialog({ workspaceId, ids, action, members, onClose, onDone }: {
    workspaceId: string;
    ids: readonly string[];
    action: BulkAction;
    members: { memberId: string; displayName: string; code: string | null }[];
    onClose: () => void;
    onDone: (message: string) => Promise<void>;
}) {
    const { money, count, today } = useFormatters();
    const [value, setValue] = useState(action === 'follow_up' ? presetDay(1, today) : '');
    const [checked, setChecked] = useState<BulkResult | null>(null);
    const [busy, setBusy] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);

    const accounts = `${count(ids.length)} ${ids.length === 1 ? 'account' : 'accounts'}`;
    const choose = (next: string) => { setValue(next); setChecked(null); setProblem(null); };
    const ready = action !== 'follow_up' || Boolean(value);

    const run = async (dryRun: boolean) => {
        if (busy || !ready) return;
        setBusy(true);
        setProblem(null);
        try {
            const result = await bulkUpdateCustomers(workspaceId, ids, action, value || null, dryRun);
            if (dryRun) setChecked(result);
            else await onDone(bulkDoneLine(result, DONE[action]));
        } catch (failed) {
            setProblem(failed instanceof Error ? failed.message : 'That did not work.');
            setChecked(null);
        } finally {
            setBusy(false);
        }
    };

    return (
        <DialogShell
            title={TITLE[action](accounts)}
            subtitle="Every account is checked against your rights first. Nothing changes until you confirm."
            size="md"
            onClose={onClose}
            closeOnEscape={!busy}
            onSubmit={e => { e.preventDefault(); void run(checked === null); }}
            footer={
                <>
                    <Button type="button" variant="quiet" disabled={busy} onClick={onClose}>Cancel</Button>
                    {checked === null ? (
                        <Button type="submit" variant="primary" busy={busy} disabled={!ready}>
                            {busy ? 'Checking…' : 'Check'}
                        </Button>
                    ) : (
                        <Button type="submit" variant="primary" busy={busy} disabled={checked.changed === 0}>
                            {busy ? 'Changing…' : `Change ${count(checked.changed)} ${checked.changed === 1 ? 'account' : 'accounts'}`}
                        </Button>
                    )}
                </>
            }
        >
            {problem && <div className="mb-4"><FormNotice tone="error">{problem}</FormNotice></div>}

            {action === 'collector' && (
                <div>
                    <label htmlFor="bulk-collector" className={labelClass}>Who works them</label>
                    <select id="bulk-collector" value={value} onChange={e => choose(e.target.value)} className={fieldClass({ className: 'w-full' })}>
                        <option value="">Nobody else — each account’s owner works it</option>
                        {members.map(m => (
                            <option key={m.memberId} value={m.memberId}>{m.displayName || m.code || 'Somebody'}</option>
                        ))}
                    </select>
                    <p className={helpClass}>Sets the collector. Each account’s owner does not change.</p>
                </div>
            )}

            {action === 'follow_up' && (
                <DateField
                    id="bulk-follow-up" label="Next follow-up" value={value} presets={[0, 1, 3, 7]}
                    notBefore={presetDay(0, today)} onChange={choose}
                    help="Accounts that owe nothing or are marked collected are left alone."
                />
            )}

            {action === 'rank' && (
                <div>
                    <label htmlFor="bulk-rank" className={labelClass}>Payment rank</label>
                    <select id="bulk-rank" value={value} onChange={e => choose(e.target.value)} className={fieldClass({ className: 'w-full' })}>
                        <option value="">Automatic — worked out from the ageing</option>
                        <option value="Good">{RANK_WORDS.Good}</option>
                        <option value="Late">{RANK_WORDS.Late}</option>
                        <option value="Bad">{RANK_WORDS.Bad}</option>
                    </select>
                    <p className={helpClass}>Bad debt takes an account out of Today’s queue. {BAD_DEBT_MEANING}</p>
                </div>
            )}

            {checked && (
                <div className="mt-5 rounded-control border border-separator bg-card-2 px-4 py-3" role="status">
                    <p className="text-body font-semibold text-label">{bulkChangedLine(checked)}</p>
                    {checked.changed > 0 && (
                        <p className="text-secondary text-label-3 mt-0.5">
                            Together they carry <span className="num text-label-2">{money.exact(checked.amount)}</span>.
                        </p>
                    )}
                    {bulkSkipLines(checked).length > 0 && (
                        <ul className="mt-2 space-y-1 text-secondary text-label-2 list-disc pl-5">
                            {bulkSkipLines(checked).map(line => <li key={line}>{line}</li>)}
                        </ul>
                    )}
                </div>
            )}
        </DialogShell>
    );
}
